// ============================================================================
// CLEO'S SECRET — CleoScorePage
// File: src/pages/CleoScorePage.jsx
//
// Full-page detail view of the patient's Cleo Score.
//   • Hero panel on OBS (dark) with the score ring as the visual centerpiece
//   • The four dimensions as DimensionCards below on PAP
//   • Improvement focus section (numbered actions)
//   • Optional score history table
//   • Disclaimer + actions (book consultation / recalculate)
//
// Inherits the dashboard pattern (header strip + maxWidth 920 main +
// SectionHead). The OBS hero is the one departure from the strict pattern,
// chosen because the score IS the page.
// ============================================================================

import React, { useState, useEffect } from 'react';
import {
  FF, FS, P, T,
  Eyebrow,
  BtnPrimary,
  Spinner,
} from '../lib/design-tokens';
import Ankh from '../components/Ankh';

// ───────── Date formatter ─────────
function formatDate(s) {
  if (!s) return '';
  return new Date(s).toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' });
}

// ───────── Score → color (matches ClientPortal) ─────────
function scoreColor(score) {
  if (score >= 80) return P.GREEN;
  if (score >= 65) return P.GO;
  if (score >= 50) return P.AMBER;
  return P.RED;
}

// ───────── Score → qualitative label ─────────
function scoreLabel(score) {
  if (score >= 90) return 'Thriving';
  if (score >= 75) return 'Good';
  if (score >= 60) return 'Moderate';
  if (score >= 40) return 'Needs attention';
  return 'Significant focus needed';
}

// ───────── Dimension keys → human label (sentence case, "and" not "&") ─────────
function dimensionLabel(key) {
  var labels = {
    hormonal: 'Hormonal balance',
    metabolic: 'Metabolic health',
    sleep: 'Sleep and recovery',
    vitality: 'Vitality and mood',
  };
  return labels[key] || key;
}

// ───────── ScoreRing — track color now themable ─────────
function ScoreRing({ size = 240, stroke = 14, score = 0, color, trackColor }) {
  var fillColor = color || P.GO;
  var track = trackColor || 'rgba(154,116,48,0.18)';
  var radius = (size - stroke) / 2;
  var circumference = 2 * Math.PI * radius;
  var offset = circumference - (score / 100) * circumference;

  return (
    <svg width={size} height={size} style={{ transform: 'rotate(-90deg)', display: 'block' }}>
      <circle cx={size/2} cy={size/2} r={radius} fill="none" stroke={track} strokeWidth={stroke} />
      <circle
        cx={size/2} cy={size/2} r={radius}
        fill="none"
        stroke={fillColor}
        strokeWidth={stroke}
        strokeDasharray={circumference}
        strokeDashoffset={offset}
        strokeLinecap="round"
        style={{ transition: 'stroke-dashoffset 1.2s ease-out' }}
      />
    </svg>
  );
}

// ───────── MiniBar — proportional fill ─────────
function MiniBar({ score }) {
  return (
    <div style={{
      width: '100%',
      height: 6,
      background: P.SAND2,
      overflow: 'hidden',
    }}>
      <div style={{
        width: (score || 0) + '%',
        height: '100%',
        background: scoreColor(score),
        transition: 'width 1.2s ease-out',
      }} />
    </div>
  );
}

// ───────── SectionHead (same scaffold as ClientPortal/MyResults) ─────────
function SectionHead({ label, count }) {
  return (
    <div style={{
      display: 'flex',
      alignItems: 'baseline',
      gap: 12,
      marginBottom: 'clamp(18px, 2vw, 24px)',
      paddingBottom: 'clamp(12px, 1.4vw, 16px)',
      borderBottom: '1px solid rgba(154,116,48,0.22)',
    }}>
      <h2 style={{ ...T.label, color: P.BR2, margin: 0 }}>{label}</h2>
      {(count !== undefined && count !== null) && (
        <span style={{
          fontFamily: FF,
          fontSize: 'clamp(12px, 1vw, 14px)',
          fontWeight: 700,
          letterSpacing: '0.34em',
          color: P.TL,
        }}>{String(count).padStart(2, '0')}</span>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// CleoScorePage
// ═══════════════════════════════════════════════════════════════════════════
export default function CleoScorePage(props) {
  var onNavigate = props.onNavigate;
  var email = props.email;

  var dataState = useState(null);
  var data = dataState[0];
  var setData = dataState[1];

  var loadingState = useState(true);
  var loading = loadingState[0];
  var setLoading = loadingState[1];

  var generatingState = useState(false);
  var generating = generatingState[0];
  var setGenerating = generatingState[1];

  function loadScores() {
    setLoading(true);
    fetch('/api/get-cleo-scores', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: email })
    })
      .then(function(res) { return res.json(); })
      .then(function(result) { setData(result); setLoading(false); })
      .catch(function() { setLoading(false); });
  }

  useEffect(function() {
    if (!email) { setLoading(false); return; }
    loadScores();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email]);

  function generateScore() {
    setGenerating(true);
    fetch('/api/get-patient-data', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: email })
    })
      .then(function(res) { return res.json(); })
      .then(function(result) {
        if (!result.patient) throw new Error('No patient record found');
        return fetch('/api/generate-cleo-score', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            patientId: result.patient.id,
            triggerEvent: 'patient_request',
          })
        });
      })
      .then(function(res) {
        if (!res.ok) return res.json().then(function(e) { throw new Error(e.error || 'Could not generate'); });
        return res.json();
      })
      .then(function() {
        loadScores();
        setGenerating(false);
      })
      .catch(function(err) {
        alert('Could not generate score: ' + err.message);
        setGenerating(false);
      });
  }

  // ───── Loading ─────
  if (loading) {
    return (
      <div style={{
        fontFamily: FF,
        background: P.PAP,
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}>
        <Spinner color={P.GO} size={48} />
      </div>
    );
  }

  var latest = data && data.latest;
  var history = (data && data.scores) || [];

  // ═════════════════════ EMPTY STATE — no score yet ═════════════════════
  if (!latest) {
    return (
      <div style={{ fontFamily: FF, background: P.PAP, minHeight: '100vh' }}>

        {/* Header strip */}
        <header style={{
          background: P.SAND,
          padding: 'clamp(36px, 4.5vw, 64px) clamp(20px, 3vw, 40px) clamp(28px, 3.5vw, 48px)',
          textAlign: 'center',
          borderBottom: '1px solid ' + P.SAND3,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}>
          <div style={{ marginBottom: 'clamp(20px, 2.2vw, 28px)', display: 'flex', justifyContent: 'center' }}>
            <Ankh responsive="opener" color={P.GO} />
          </div>
          <div style={{ marginBottom: 16 }}>
            <Eyebrow center>Cleo Score</Eyebrow>
          </div>
          <h1 style={{
            fontFamily: FF,
            fontSize: 'clamp(28px, 3.4vw, 44px)',
            fontWeight: 400,
            lineHeight: 1.12,
            letterSpacing: '-0.012em',
            color: P.INK,
            margin: 0,
          }}>
            Your <span style={{ fontStyle: 'italic', color: P.BR2 }}>Cleo Score.</span>
          </h1>
        </header>

        {/* Empty state body */}
        <main style={{
          maxWidth: 640,
          margin: '0 auto',
          padding: 'clamp(48px, 6vw, 80px) clamp(24px, 3vw, 40px)',
          textAlign: 'center',
        }}>
          <p style={{
            ...T.premise,
            fontSize: 'clamp(20px, 2vw, 28px)',
            color: P.BR2,
            marginBottom: 'clamp(20px, 2.4vw, 28px)',
          }}>Begin your conversation with Cleo to generate your score.</p>

          <p style={{
            ...T.bodyLg,
            color: P.TX,
            textAlign: 'justify',
            hyphens: 'auto',
            marginBottom: 'clamp(28px, 3vw, 40px)',
            lineHeight: 1.85,
          }}>
            Your Cleo Score is a personalised health metric measured across four dimensions:
            hormonal balance, metabolic health, sleep and recovery, and vitality and mood.
            Once you complete your health conversation with Cleo, your score will appear here automatically.
          </p>

          <BtnPrimary onClick={generateScore} disabled={generating} large>
            {generating ? 'Calculating\u2026' : 'Generate my Cleo Score'}
          </BtnPrimary>
        </main>
      </div>
    );
  }

  // ═════════════════════ MAIN — has a latest score ═════════════════════
  var overallColor = scoreColor(latest.overall_score);
  var focusKey = latest.improvement_focus;

  // ───── DimensionCard (closure over latest, focusKey) ─────
  function DimensionCard({ dimKey }) {
    var score = latest[dimKey + '_score'];
    var reasoning = latest[dimKey + '_reasoning'];
    var isFocus = dimKey === focusKey;

    return (
      <div style={{
        padding: 'clamp(20px, 2.4vw, 28px)',
        background: isFocus ? 'rgba(201,169,97,0.05)' : P.PAP,
        border: '1px solid ' + P.SAND3,
        borderLeft: isFocus ? '3px solid ' + P.GO : '1px solid ' + P.SAND3,
        marginBottom: 14,
      }}>
        {isFocus && (
          <div style={{
            ...T.label,
            fontSize: 'clamp(10px, 0.85vw, 12px)',
            color: P.BR2,
            marginBottom: 10,
          }}>Your focus</div>
        )}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-end',
          gap: 16,
          marginBottom: 14,
        }}>
          <h3 style={{
            fontFamily: FF,
            fontSize: 'clamp(18px, 1.6vw, 22px)',
            fontWeight: 500,
            color: P.INK,
            lineHeight: 1.2,
            margin: 0,
            letterSpacing: '-0.005em',
          }}>{dimensionLabel(dimKey)}</h3>
          <div style={{
            fontFamily: FF,
            fontSize: 'clamp(32px, 3.4vw, 44px)',
            fontWeight: 400,
            color: scoreColor(score),
            lineHeight: 1,
            letterSpacing: '-0.015em',
          }}>{score}</div>
        </div>
        <div style={{ marginBottom: 16 }}>
          <MiniBar score={score} />
        </div>
        <p style={{
          ...T.bodyMd,
          color: P.TL,
          lineHeight: 1.75,
          margin: 0,
        }}>{reasoning}</p>
      </div>
    );
  }

  return (
    <div style={{ fontFamily: FF, background: P.PAP, minHeight: '100vh' }}>

      {/* ═════════ Header strip ═════════ */}
      <header style={{
        background: P.SAND,
        padding: 'clamp(36px, 4.5vw, 64px) clamp(20px, 3vw, 40px) clamp(28px, 3.5vw, 48px)',
        textAlign: 'center',
        borderBottom: '1px solid ' + P.SAND3,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
      }}>
        <div style={{ marginBottom: 'clamp(20px, 2.2vw, 28px)', display: 'flex', justifyContent: 'center' }}>
          <Ankh responsive="opener" color={P.GO} />
        </div>
        <div style={{ marginBottom: 16 }}>
          <Eyebrow center>Cleo Score</Eyebrow>
        </div>
        <h1 style={{
          fontFamily: FF,
          fontSize: 'clamp(28px, 3.4vw, 44px)',
          fontWeight: 400,
          lineHeight: 1.12,
          letterSpacing: '-0.012em',
          color: P.INK,
          margin: 0,
        }}>
          Your <span style={{ fontStyle: 'italic', color: P.BR2 }}>Cleo Score.</span>
        </h1>
        <div style={{
          ...T.bodySm,
          color: P.TF,
          marginTop: 10,
          letterSpacing: '0.04em',
        }}>
          Updated {formatDate(latest.generated_at)}
        </div>
      </header>

      {/* ═════════ Main ═════════ */}
      <main style={{
        maxWidth: 920,
        margin: '0 auto',
        padding: 'clamp(32px, 4vw, 56px) clamp(20px, 3vw, 40px) clamp(64px, 8vw, 120px)',
      }}>

        {/* ─── Hero score panel (OBS dark) ─── */}
        <section style={{
          background: P.OBS,
          padding: 'clamp(40px, 5vw, 72px) clamp(24px, 4vw, 48px)',
          textAlign: 'center',
          marginBottom: 'clamp(40px, 4.5vw, 64px)',
          color: P.IVY,
        }}>
          {/* Decorative top divider */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 12,
            marginBottom: 'clamp(28px, 3vw, 40px)',
          }}>
            <div style={{ width: 'clamp(40px, 8vw, 80px)', height: 1, background: P.GO2, opacity: 0.5 }} />
            <div style={{ width: 6, height: 6, background: P.GO2, transform: 'rotate(45deg)', opacity: 0.7 }} />
            <div style={{ width: 'clamp(40px, 8vw, 80px)', height: 1, background: P.GO2, opacity: 0.5 }} />
          </div>

          {/* Ring + score */}
          <div style={{ position: 'relative', display: 'inline-block' }}>
            <ScoreRing
              size={280}
              stroke={14}
              score={latest.overall_score}
              color={overallColor}
              trackColor="rgba(232,221,196,0.12)"
            />
            <div style={{
              position: 'absolute',
              top: 0, left: 0, right: 0, bottom: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              <div style={{
                fontFamily: FF,
                fontSize: 'clamp(64px, 9vw, 96px)',
                fontWeight: 400,
                color: P.PAP,
                lineHeight: 1,
                letterSpacing: '-0.025em',
              }}>{latest.overall_score}</div>
              <div style={{
                fontFamily: FF,
                fontSize: 'clamp(11px, 1vw, 14px)',
                fontWeight: 700,
                letterSpacing: '0.34em',
                textTransform: 'uppercase',
                color: overallColor,
                marginTop: 12,
              }}>{scoreLabel(latest.overall_score)}</div>
            </div>
          </div>

          {/* Pattern */}
          {latest.pattern && (
            <div style={{ marginTop: 'clamp(32px, 3.6vw, 48px)' }}>
              <div style={{
                fontFamily: FF,
                fontSize: 'clamp(11px, 0.95vw, 13px)',
                fontWeight: 700,
                letterSpacing: '0.34em',
                textTransform: 'uppercase',
                color: P.GO2,
                marginBottom: 12,
              }}>Your pattern</div>
              <div style={{
                fontFamily: FF,
                fontSize: 'clamp(22px, 2.4vw, 32px)',
                fontWeight: 400,
                fontStyle: 'italic',
                color: P.PAP,
                lineHeight: 1.3,
                letterSpacing: '-0.005em',
              }}>{latest.pattern}</div>
            </div>
          )}

          {/* Pattern description */}
          {latest.pattern_description && (
            <p style={{
              fontFamily: FF,
              fontSize: 'clamp(15px, 1.3vw, 18px)',
              fontStyle: 'italic',
              color: 'rgba(240,232,212,0.86)',
              lineHeight: 1.7,
              marginTop: 'clamp(20px, 2.2vw, 28px)',
              maxWidth: 560,
              margin: 'clamp(20px, 2.2vw, 28px) auto 0',
            }}>{latest.pattern_description}</p>
          )}
        </section>

        {/* ─── Four dimensions ─── */}
        <section style={{ marginBottom: 'clamp(40px, 4.5vw, 64px)' }}>
          <SectionHead label="The four dimensions" count={4} />
          <DimensionCard dimKey="hormonal" />
          <DimensionCard dimKey="metabolic" />
          <DimensionCard dimKey="sleep" />
          <DimensionCard dimKey="vitality" />
        </section>

        {/* ─── Improvement focus ─── */}
        {latest.improvement_actions && latest.improvement_actions.length > 0 && (
          <section style={{ marginBottom: 'clamp(40px, 4.5vw, 64px)' }}>
            <SectionHead label="Your improvement focus" count={latest.improvement_actions.length} />

            <h3 style={{
              fontFamily: FF,
              fontSize: 'clamp(20px, 2vw, 26px)',
              fontWeight: 400,
              fontStyle: 'italic',
              color: P.BR2,
              lineHeight: 1.3,
              margin: '0 0 clamp(14px, 1.6vw, 20px)',
              letterSpacing: '-0.005em',
            }}>
              Lifting your {dimensionLabel(focusKey).toLowerCase()} score.
            </h3>

            <p style={{
              ...T.bodyMd,
              color: P.TL,
              lineHeight: 1.75,
              marginBottom: 'clamp(24px, 2.6vw, 32px)',
              textAlign: 'justify',
              hyphens: 'auto',
            }}>
              This dimension has the greatest potential to lift your overall Cleo Score. Concrete steps follow.
            </p>

            <ol style={{
              listStyle: 'none',
              padding: 0,
              margin: 0,
              borderTop: '1px solid ' + P.SAND3,
            }}>
              {latest.improvement_actions.map(function(action, i) {
                return (
                  <li key={i} style={{
                    display: 'grid',
                    gridTemplateColumns: 'clamp(48px, 6vw, 72px) 1fr',
                    gap: 'clamp(16px, 1.8vw, 24px)',
                    padding: 'clamp(18px, 2vw, 26px) 0',
                    borderBottom: '1px solid ' + P.SAND3,
                  }}>
                    <div style={{
                      fontFamily: FF,
                      fontSize: 'clamp(13px, 1.1vw, 15px)',
                      fontWeight: 700,
                      letterSpacing: '0.34em',
                      color: P.GO,
                      paddingTop: 4,
                    }}>{String(i + 1).padStart(2, '0')}</div>
                    <p style={{
                      ...T.bodyMd,
                      color: P.TX,
                      lineHeight: 1.75,
                      margin: 0,
                    }}>{action}</p>
                  </li>
                );
              })}
            </ol>
          </section>
        )}

        {/* ─── Score history ─── */}
        {history.length > 1 && (
          <section style={{ marginBottom: 'clamp(40px, 4.5vw, 64px)' }}>
            <SectionHead label="Your score history" count={history.length} />
            <div style={{ borderTop: '1px solid ' + P.SAND3 }}>
              {history.map(function(s) {
                return (
                  <div key={s.id} style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: 16,
                    padding: 'clamp(16px, 1.8vw, 22px) 0',
                    borderBottom: '1px solid ' + P.SAND3,
                  }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{
                        fontFamily: FF,
                        fontSize: 'clamp(15px, 1.3vw, 17px)',
                        fontWeight: 500,
                        color: P.INK,
                        marginBottom: 4,
                      }}>{formatDate(s.generated_at)}</div>
                      {s.pattern && (
                        <div style={{
                          fontFamily: FF,
                          fontSize: 'clamp(13px, 1.1vw, 15px)',
                          fontStyle: 'italic',
                          color: P.TF,
                          lineHeight: 1.4,
                        }}>{s.pattern}</div>
                      )}
                    </div>
                    <div style={{
                      fontFamily: FF,
                      fontSize: 'clamp(28px, 2.8vw, 36px)',
                      fontWeight: 400,
                      color: scoreColor(s.overall_score),
                      lineHeight: 1,
                      letterSpacing: '-0.012em',
                      flexShrink: 0,
                    }}>{s.overall_score}</div>
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {/* ─── Disclaimer ─── */}
        <div style={{
          ...T.bodySm,
          fontSize: 'clamp(12px, 0.95vw, 13px)',
          color: P.TF,
          fontStyle: 'italic',
          textAlign: 'center',
          lineHeight: 1.75,
          padding: 'clamp(16px, 1.8vw, 22px) 0',
          marginBottom: 'clamp(24px, 2.6vw, 32px)',
        }}>
          Your Cleo Score is a personalised wellness metric, not a clinical diagnosis. Discuss any concerns with your GP.
        </div>

        {/* ─── Actions ─── */}
        <div style={{
          display: 'flex',
          gap: 12,
          flexWrap: 'wrap',
        }} className="cs-score-actions">
          <div style={{ flex: 1, minWidth: 240 }}>
            <button
              onClick={function() { if (onNavigate) onNavigate('scheduler'); }}
              style={{
                width: '100%',
                fontFamily: FS,
                fontSize: 'clamp(12px, 1.1vw, 14px)',
                fontWeight: 600,
                letterSpacing: '0.28em',
                textTransform: 'uppercase',
                padding: 'clamp(18px, 1.8vw, 22px) clamp(28px, 4vw, 48px)',
                background: P.INK,
                color: P.PAP,
                border: 'none',
                cursor: 'pointer',
                transition: 'background 0.25s, color 0.25s',
              }}
              onMouseEnter={function(e) {
                e.currentTarget.style.background = P.BR;
              }}
              onMouseLeave={function(e) {
                e.currentTarget.style.background = P.INK;
              }}>
              Book a consultation to discuss
            </button>
          </div>
          <div style={{ flex: 1, minWidth: 240 }}>
            <button
              onClick={generateScore}
              disabled={generating}
              style={{
                width: '100%',
                fontFamily: FS,
                fontSize: 'clamp(12px, 1.1vw, 14px)',
                fontWeight: 600,
                letterSpacing: '0.28em',
                textTransform: 'uppercase',
                padding: 'clamp(18px, 1.8vw, 22px) clamp(28px, 4vw, 48px)',
                background: 'transparent',
                color: P.BR,
                border: '1px solid ' + P.GO,
                cursor: generating ? 'default' : 'pointer',
                opacity: generating ? 0.5 : 1,
                transition: 'all 0.25s',
              }}
              onMouseEnter={function(e) {
                if (generating) return;
                e.currentTarget.style.background = 'rgba(201,169,97,0.08)';
              }}
              onMouseLeave={function(e) {
                if (generating) return;
                e.currentTarget.style.background = 'transparent';
              }}>
              {generating ? 'Recalculating\u2026' : 'Recalculate my score'}
            </button>
          </div>
        </div>

      </main>

      <style>{`
        @media (max-width: 640px) {
          .cs-score-actions > div { min-width: 100% !important; }
        }
      `}</style>
    </div>
  );
}
